import {
  PlusCircleIcon,
  Trash2Icon,
  PencilIcon,
  Link2Icon,
  ScissorsIcon,
  PlayIcon,
  WorkflowIcon,
} from "lucide-react";
import { useAssistantToolUI, useInlineRender } from "@assistant-ui/react";
import { usePipelineStore } from "@/lib/stores/pipeline-store";
import { ToolChip } from "./ToolChip";

// assistant-ui may pass result as a JSON string or Python dict string — normalize to object
function parseResult(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {}
    // Fallback: Python dict format where ALL values use single quotes (e.g. {'key': 'val'}).
    // Note: we do NOT attempt a blanket ' → " replacement because the backend often mixes
    // quote styles (keys in single quotes, values in double quotes) which breaks JSON.parse.
  }
  if (typeof raw === "object") return raw as Record<string, unknown>;
  return null;
}

/**
 * Extract the error message from a tool result, regardless of format.
 * Handles plain objects, JSON strings, and Python-style dict strings.
 */
function extractErrorMessage(raw: unknown): string | null {
  if (!raw) return null;

  // Plain object
  if (typeof raw === "object") {
    const r = raw as Record<string, unknown>;
    if (r.error)
      return typeof r.error === "string" ? r.error : JSON.stringify(r.error);
    return null;
  }

  if (typeof raw !== "string") return null;

  // Standard JSON
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed.error)
      return typeof parsed.error === "string"
        ? parsed.error
        : JSON.stringify(parsed.error);
  } catch {}

  // Python-style dict: 'error': "message" (key in single quotes, value in double quotes)
  const m1 = raw.match(/'error'\s*:\s*"([^"]*)"/);
  if (m1) return m1[1];

  // Python-style dict: 'error': 'message' (both in single quotes)
  const m2 = raw.match(/'error'\s*:\s*'([^']*)'/);
  if (m2) return m2[1];

  return null;
}

// ── add_node ──────────────────────────────────────────────────────────────────

export function AddNodeToolUI() {
  const focusNode = usePipelineStore((s) => s.focusNode);

  useAssistantToolUI({
    toolName: "add_node",
    render: useInlineRender(({ argsText, args, result, status, isError }) => {
      const parsed = parseResult(result);
      const node = parsed?.node as
        | { id?: string; data?: { label?: string } }
        | undefined;
      const nodeId = node?.id ?? null;
      const resultError =
        isError
          ? (extractErrorMessage(result) ?? "Tool call failed")
          : extractErrorMessage(result);
      // Label from result; fall back to args (reliable even when result parsing is partial)
      const label =
        node?.data?.label ??
        (args && typeof args === "object" && "label" in args
          ? ((args as { label?: string }).label ?? null)
          : null);

      return (
        <ToolChip
          icon={PlusCircleIcon}
          runningVerb="Adding..."
          completeVerb="Added"
          failedVerb="Failed to add node"
          subject={resultError ? undefined : (label ?? undefined)}
          status={status}
          argsText={argsText}
          result={result}
          resultError={resultError}
          onChipClick={
            nodeId && !resultError ? () => focusNode(nodeId) : undefined
          }
        />
      );
    }),
  });

  return null;
}

// ── remove_node ───────────────────────────────────────────────────────────────

export function RemoveNodeToolUI() {
  useAssistantToolUI({
    toolName: "remove_node",
    render: useInlineRender(({ argsText, result, status, isError }) => {
      const resultError =
        isError
          ? (extractErrorMessage(result) ?? "Tool call failed")
          : extractErrorMessage(result);

      return (
        <ToolChip
          icon={Trash2Icon}
          runningVerb="Removing..."
          completeVerb="Removed node"
          failedVerb="Failed to remove node"
          status={status}
          argsText={argsText}
          result={result}
          resultError={resultError}
        />
      );
    }),
  });

  return null;
}

// ── update_node ───────────────────────────────────────────────────────────────

export function UpdateNodeToolUI() {
  const focusNode = usePipelineStore((s) => s.focusNode);

  useAssistantToolUI({
    toolName: "update_node",
    render: useInlineRender(({ argsText, result, status, isError }) => {
      const parsed = parseResult(result);
      const node = parsed?.node as
        | { id?: string; data?: { label?: string } }
        | undefined;
      const nodeId = node?.id ?? null;
      const resultError =
        isError
          ? (extractErrorMessage(result) ?? "Tool call failed")
          : extractErrorMessage(result);
      const label = node?.data?.label ?? null;

      return (
        <ToolChip
          icon={PencilIcon}
          runningVerb="Updating..."
          completeVerb="Updated"
          failedVerb="Failed to update node"
          subject={resultError ? undefined : (label ?? undefined)}
          status={status}
          argsText={argsText}
          result={result}
          resultError={resultError}
          onChipClick={
            nodeId && !resultError ? () => focusNode(nodeId) : undefined
          }
        />
      );
    }),
  });

  return null;
}

// ── connect_nodes ─────────────────────────────────────────────────────────────

export function ConnectNodesToolUI() {
  const focusNode = usePipelineStore((s) => s.focusNode);

  useAssistantToolUI({
    toolName: "connect_nodes",
    render: useInlineRender(({ argsText, args, result, status, isError }) => {
      const resultError =
        isError
          ? (extractErrorMessage(result) ?? "Tool call failed")
          : extractErrorMessage(result);
      const targetId =
        args && typeof args === "object" && "target_id" in (args as object)
          ? ((args as { target_id: string }).target_id ?? null)
          : null;

      return (
        <ToolChip
          icon={Link2Icon}
          runningVerb="Connecting..."
          completeVerb="Connected nodes"
          failedVerb="Failed to connect nodes"
          status={status}
          argsText={argsText}
          result={result}
          resultError={resultError}
          onChipClick={
            targetId && !resultError ? () => focusNode(targetId) : undefined
          }
        />
      );
    }),
  });

  return null;
}

// ── remove_edge ───────────────────────────────────────────────────────────────

export function RemoveEdgeToolUI() {
  useAssistantToolUI({
    toolName: "remove_edge",
    render: useInlineRender(({ argsText, result, status, isError }) => {
      const resultError =
        isError
          ? (extractErrorMessage(result) ?? "Tool call failed")
          : extractErrorMessage(result);

      return (
        <ToolChip
          icon={ScissorsIcon}
          runningVerb="Removing..."
          completeVerb="Removed connection"
          failedVerb="Failed to remove connection"
          status={status}
          argsText={argsText}
          result={result}
          resultError={resultError}
        />
      );
    }),
  });

  return null;
}

// ── run_pipeline ──────────────────────────────────────────────────────────────

export function RunPipelineToolUI() {
  useAssistantToolUI({
    toolName: "run_pipeline",
    render: useInlineRender(({ argsText, result, status, isError }) => {
      const resultError =
        isError
          ? (extractErrorMessage(result) ?? "Tool call failed")
          : extractErrorMessage(result);

      return (
        <ToolChip
          icon={PlayIcon}
          runningVerb="Starting pipeline..."
          completeVerb="Pipeline started"
          failedVerb="Failed to start pipeline"
          status={status}
          argsText={argsText}
          result={result}
          resultError={resultError}
        />
      );
    }),
  });

  return null;
}

// ── build_pipeline ────────────────────────────────────────────────────────────

export function BuildPipelineToolUI() {
  useAssistantToolUI({
    toolName: "build_pipeline",
    render: useInlineRender(({ argsText, result, status, isError }) => {
      const parsed = parseResult(result);
      const nodes = Array.isArray(parsed?.nodes) ? (parsed.nodes as unknown[]) : [];
      const resultError =
        isError
          ? (extractErrorMessage(result) ?? "Tool call failed")
          : extractErrorMessage(result);
      const subject =
        !resultError && nodes.length > 0
          ? `${nodes.length} node${nodes.length !== 1 ? "s" : ""}`
          : undefined;

      return (
        <ToolChip
          icon={WorkflowIcon}
          runningVerb="Building pipeline..."
          completeVerb="Built pipeline"
          failedVerb="Failed to build pipeline"
          subject={subject}
          status={status}
          argsText={argsText}
          result={result}
          resultError={resultError}
        />
      );
    }),
  });

  return null;
}
